import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../auth/useAuth';
import { hasRole } from '../auth/roles';
import { useMediaQuery } from '../lib/useMediaQuery';
import { useTheme } from '../theme/useTheme';
import { InstallPrompt } from '../components/InstallPrompt';
import { MatchdayPrompt } from '../features/matchday/MatchdayPrompt';
import { PaymentPrompt } from '../features/myBookings/PaymentPrompt';
import './AppShell.css';

/**
 * Role-aware shell. Nav config stays here (data-first) — on narrow screens the links collapse
 * behind a hamburger into a slide-down panel (admins carry ~10 links; a phone header can't).
 */
export function AppShell() {
  const { branding, scheme, toggleScheme } = useTheme();
  const { user, logout } = useAuth();
  const [navOpen, setNavOpen] = useState(false);
  // The payment prompt queues behind the matchday check-in. Its dismissal only writes
  // sessionStorage, which re-renders nothing — this state is the reactive signal that lets the
  // payment prompt appear the moment the matchday one is waved away, not on the next navigation.
  const [matchdayDismissed, setMatchdayDismissed] = useState(false);

  const hasNav = !!user && user.status === 'Active';
  const isAdmin = hasNav && hasRole(user.role, 'Admin');
  // Where the nav collapses behind the hamburger depends on how many links the role carries,
  // which CSS alone can't know: a coach's 3 links fit a small laptop, an admin's ~13 overflow
  // anything under ~1450px and wrapped untidily mid-label before collapsing.
  const navCollapsed = useMediaQuery(isAdmin ? '(max-width: 1450px)' : '(max-width: 1150px)');

  // Widening past the breakpoint removes the hamburger; drop any open panel with it.
  useEffect(() => {
    if (!navCollapsed) setNavOpen(false);
  }, [navCollapsed]);

  return (
    <div className="app-shell">
      <header className={`app-header ${navCollapsed ? 'app-header-collapsed' : ''}`}>
        <Link to="/" className="app-header-title">
          {branding?.logoUrl ? (
            <img className="app-header-logo" src={branding.logoUrl} alt="" />
          ) : null}
          <span>{branding?.clubName ?? 'Coach Portal'}</span>
        </Link>
        <nav
          id="app-nav"
          className={`app-nav ${navOpen ? 'app-nav-open' : ''}`}
          aria-label="Main"
          // Delegated: any link tap closes the mobile panel (no-op on desktop).
          onClick={(e) => {
            if ((e.target as HTMLElement).closest('a')) setNavOpen(false);
          }}
        >
          {hasNav ? (
            <>
              <NavLink to="/book" className="app-nav-link">
                Book
              </NavLink>
              <NavLink to="/my-bookings" className="app-nav-link">
                My bookings
              </NavLink>
              {/* "My payments", not "Payments" — admins carry an admin Payments link too. */}
              <NavLink to="/my-payments" className="app-nav-link">
                My payments
              </NavLink>
              {hasRole(user.role, 'Admin') ? (
                <>
                  {/* `end` keeps this from lighting up on every /admin/* page. */}
                  <NavLink to="/admin" end className="app-nav-link">
                    Dashboard
                  </NavLink>
                  <NavLink to="/admin/schedule" className="app-nav-link">
                    Schedule
                  </NavLink>
                  <NavLink to="/admin/matchdays" className="app-nav-link">
                    Matchdays
                  </NavLink>
                  <NavLink to="/admin/requests" className="app-nav-link">
                    Requests
                  </NavLink>
                  <NavLink to="/admin/teams" className="app-nav-link">
                    Teams
                  </NavLink>
                  <NavLink to="/admin/venues" className="app-nav-link">
                    Venues
                  </NavLink>
                  <NavLink to="/admin/pricing" className="app-nav-link">
                    Pricing
                  </NavLink>
                  <NavLink to="/admin/payments" className="app-nav-link">
                    Payments
                  </NavLink>
                  <NavLink to="/admin/users" className="app-nav-link">
                    Users
                  </NavLink>
                  {hasRole(user.role, 'Developer') ? (
                    <NavLink to="/dev/settings" className="app-nav-link">
                      Dev
                    </NavLink>
                  ) : null}
                </>
              ) : null}
              {/* Mobile-only menu item — the header keeps Sign out on desktop, where there is no menu. */}
              <button
                type="button"
                className="app-nav-signout"
                onClick={() => {
                  setNavOpen(false);
                  void logout();
                }}
              >
                Sign out
              </button>
            </>
          ) : null}
        </nav>
        <div className="app-header-actions">
          <button
            type="button"
            className="scheme-toggle"
            onClick={toggleScheme}
            aria-label={`Switch to ${scheme === 'light' ? 'dark' : 'light'} mode`}
          >
            {scheme === 'light' ? '☾' : '☀'}
          </button>
          {user ? (
            // With a nav menu the mobile header hides this (Sign out lives at the menu's foot);
            // without one (pending user) it must stay visible everywhere or they can't sign out.
            <button
              type="button"
              className={`app-signout ${hasNav ? 'app-signout-desktop' : ''}`}
              onClick={() => void logout()}
            >
              Sign out
            </button>
          ) : (
            <Link to="/login" className="app-signout">
              Sign in
            </Link>
          )}
          {hasNav ? (
            <button
              type="button"
              className="app-nav-toggle"
              aria-expanded={navOpen}
              aria-controls="app-nav"
              aria-label={navOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setNavOpen((open) => !open)}
            >
              {navOpen ? <X size={22} aria-hidden /> : <Menu size={22} aria-hidden />}
            </button>
          ) : null}
        </div>
      </header>
      {navOpen ? (
        <div className="app-nav-backdrop" role="presentation" onClick={() => setNavOpen(false)} />
      ) : null}
      <main className="app-main">
        <Outlet />
      </main>
      <MatchdayPrompt onDismiss={() => setMatchdayDismissed(true)} />
      <PaymentPrompt matchdayDismissed={matchdayDismissed} />
      <InstallPrompt />
    </div>
  );
}
